import { Navigate, useLocation } from "@solidjs/router";
import { Match, type ParentComponent, Show, Switch } from "solid-js";
import { Loading } from "@/components/ui/loading";
import { buildLoginUrlWithNext } from "@/libs/redirect";
import { useAuthV2 } from "../context/auth-provider";
import { getNextStepPath } from "../session/navigation";

const PUBLISHER_PERSONAL_ONBOARDING_ALLOWED_PATHS = [
  "/profile/company",
  "/job-offers/create",
] as const;

export const canAccessPublisherPathBeforePersonalOnboarding = (
  pathname: string,
) =>
  PUBLISHER_PERSONAL_ONBOARDING_ALLOWED_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );

export const ProtectedRoute: ParentComponent = (props) => {
  const [authStoreV2] = useAuthV2();
  const location = useLocation();
  const pathname = location.pathname;
  const nextStepPath = () => getNextStepPath(authStoreV2?.next_step);
  const shouldForceNextStep = () => {
    const target = nextStepPath();
    if (!target) return false;
    if (
      authStoreV2?.next_step === "complete_personal_profile" &&
      authStoreV2?.capabilities?.can_publish &&
      canAccessPublisherPathBeforePersonalOnboarding(pathname)
    ) {
      return false;
    }
    if (
      authStoreV2?.next_step !== "choose_intent" &&
      authStoreV2?.next_step !== "complete_personal_profile" &&
      authStoreV2?.next_step !== "complete_workspace_onboarding" &&
      authStoreV2?.next_step !== "complete_company_onboarding" &&
      authStoreV2?.next_step !== "convert_solo_freelancer_to_company"
    ) {
      return false;
    }
    return !pathname.startsWith(target);
  };

  return (
    <Show when={!authStoreV2?.loading} fallback={<Loading />}>
      <Switch>
        <Match when={!authStoreV2?.profile?.id}>
          <Navigate href={buildLoginUrlWithNext(pathname)} />
        </Match>
        <Match when={shouldForceNextStep()}>
          <Navigate href={nextStepPath() ?? "/"} />
        </Match>
        <Match
          when={
            !authStoreV2?.next_step &&
            authStoreV2?.profile?.onboarding_status === "in_progress" &&
            !authStoreV2?.capabilities?.can_publish &&
            !pathname.includes("onboarding")
          }
        >
          <Show
            when={authStoreV2?.profile?.profile_type === "provider"}
            fallback={<Navigate href="/auth/onboarding" />}
          >
            <Navigate href="/auth/onboarding/company" />
          </Show>
        </Match>
        <Match when={authStoreV2?.profile?.id}>{props.children}</Match>
      </Switch>
    </Show>
  );
};
